import React, { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { localized, localizedInterpolation } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { WhiteText } from 'web/view/components/Text/white-text';
import PageContainer from 'web/view/components/page-container/page-container';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { GetAnimalListPdf, GetDadMomTablePdf } from 'web/web-helpers/pdf-helper/animal-list-helper';

import { RawAxisProps, SeriesProps } from '@devexpress/dx-react-chart';
import moment from 'moment';
import { DashboardTableModelItem } from 'shared/api/api';
import { Colors } from 'shared/assets/root-assets/colors';
import { Heading } from '../utils/heading';
import { BarChart } from '../graph/bar-graph/bar-chart';
import { GetMatingBatchReportGraphBySiteAndDate } from 'shared/state/ducks/mating-batch-report/operations';
import { ClipLoader } from 'react-spinners';
import { isSameDate } from 'shared/helpers/date-helpers';

interface PropsFromParent {
	closeModal: () => void;
	date: Date;
}

export const BatchReportMatingBarGRaph: FC<PropsFromParent> = React.memo(({ closeModal, date }) => {
	const dispatch = useDispatch();
	// report data
	const matingReport = useSelector(
		(state: WebAppState) => state.matingBatchReports.matingBatchReportsGraph.matingReport,
	);
	const { loadingGraph } = useSelector((state: WebAppState) => state.matingBatchReports);
	const matingBathces = useSelector((state: WebAppState) => state.matingBatches.entities);
	const { siteId } = useSelector((state: WebAppState) => state.profile.active!);
	useEffect(() => {
		if (siteId) {
			dispatch(GetMatingBatchReportGraphBySiteAndDate(date, siteId));
		}
	}, [siteId]);
	// Set bar series
	const series = useMemo(() => {
		return [
			{
				argumentField: 'weekYear',
				valueField: 'totalMatings',
				name: 'totalMatings',
				color: Colors.darkGreen,
				key: 'totalMatings',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'youngAnimalMatings',
				name: 'youngAnimalMatings',
				color: Colors.lightGreen,
				key: 'youngAnimalMatings',
			} as SeriesProps,
			{
				argumentField: 'weekYear',
				valueField: 'returnSows',
				name: 'returnSows',
				color: Colors.darkRed,
				key: 'returnSows',
			} as SeriesProps,
		];
	}, []);

	//  set YAxis
	const yAxis = useMemo(() => {
		return [{ position: 'left' } as RawAxisProps];
	}, []);

	// generate bar chart data
	const data = useMemo(() => {
		if (!matingReport) {
			return [];
		}

		let data: any[] = [];

		matingReport.forEach(element => {
			let objectData = { ...element } as any;

			const date = moment(element.matingBatch?.matingPeriodStart);
			const batch = matingBathces.find(x =>
				isSameDate(x.matingPeriodStart, element.matingBatch?.matingPeriodStart),
			);
			const key = batch?.batchNumber + '-' + date.year();

			objectData.weekYear = key;
			objectData.date = element.matingBatch?.matingPeriodStart;
			objectData.totalMatings -= objectData.youngAnimalMatings;
			objectData.totalMatings -= objectData.returnSows;
			data.push(objectData);
		});

		return data.slice().sort((a, b) => a.date - b.date);
	}, [matingReport]);

	return (
		<PageContainer>
			<Heading text={localized('Matings')} />
			<ViewWeb className="default-spacing">
				<ViewWeb className="default-close-pos">
					<SkioldButton theme="grey" onPress={closeModal} title={localized('Close')} />
				</ViewWeb>
				{loadingGraph ? (
					<ViewWeb className="spinner-container-active">
						<ClipLoader color="#f2ac40" size={70} />
					</ViewWeb>
				) : (
					<ViewWeb className="flex-row">
						<ViewWeb className="vertical-legend-title">
							<div className="rotate-container">
								<WhiteText className="header-white-text rotate-270">
									{localized('MatingsPerBatchPcs')}
								</WhiteText>
							</div>
						</ViewWeb>
						<ViewWeb className="flexOne">
							<BarChart
								barSeries={series}
								chartData={data}
								valueAxis={yAxis}
								title=""
								stack={true}
								keyTranslator={localized}
								zoomPan="both"
								verticalArgumentLabels={true}
							/>
							<WhiteText className="header-white-text">{localized('matingBatch')}</WhiteText>
						</ViewWeb>
					</ViewWeb>
				)}
			</ViewWeb>
		</PageContainer>
	);
});
